import api from './api'

const admin = (path) => `/admin${path}`

export const userService = {
  getUsers: async (params) => {
    const res = await api.get(admin('/users'), { params })
    return res.items || []
  },
  getUser: async (id) => {
    return await api.get(admin(`/users/${id}`))
  },
  createUser: async (userData) => {
    return await api.post(admin('/users'), userData)
  },
  updateUser: async (id, userData) => {
    return await api.put(admin(`/users/${id}`), userData)
  },
  deleteUser: async (id) => {
    return await api.delete(admin(`/users/${id}`))
  }
}

export const adminStatsService = {
  getStats: async () => {
    return await api.get(admin('/stats'))
  }
}

export const adminVendorsService = {
  list: async (params) => {
    const res = await api.get(admin('/vendors'), { params: params || {} })
    return res.items || []
  },
  getById: async (id) => {
    return await api.get(admin(`/vendors/${id}`), { timeout: 60000 })
  },
  create: async (data) => {
    return await api.post(admin('/vendors'), data, { timeout: 60000 })
  },
  update: async (id, data) => {
    return await api.put(admin(`/vendors/${id}`), data, { timeout: 60000 })
  },
  updateStatus: async (id, status) => {
    return await api.patch(admin(`/vendors/${id}`), { status })
  }
}

export const adminVendorBookingsService = {
  getByVendorId: async (vendorId) => {
    const res = await api.get(admin(`/vendors/${vendorId}/bookings`))
    return res.items || []
  }
}

// Longer timeout for plan create/update (base64 images = large payloads)
const PLAN_REQUEST_TIMEOUT = 60000

export const adminPlansService = {
  list: async () => {
    const res = await api.get(admin('/plans'), { timeout: PLAN_REQUEST_TIMEOUT })
    return res.items || []
  },
  getById: async (id) => {
    return await api.get(admin(`/plans/${id}`))
  },
  create: async (data) => {
    return await api.post(admin('/plans'), data, { timeout: PLAN_REQUEST_TIMEOUT })
  },
  update: async (id, data) => {
    return await api.put(admin(`/plans/${id}`), data, { timeout: PLAN_REQUEST_TIMEOUT })
  },
  delete: async (id) => {
    return await api.delete(admin(`/plans/${id}`))
  }
}

export const adminCategoriesService = {
  list: async () => {
    const res = await api.get(admin('/categories'))
    return res.items || []
  },
  create: async (data) => {
    return await api.post(admin('/categories'), data)
  },
  update: async (id, data) => {
    return await api.put(admin(`/categories/${id}`), data)
  },
  delete: async (id) => {
    return await api.delete(admin(`/categories/${id}`))
  }
}

export const adminVendorCategoriesService = {
  list: async () => {
    const res = await api.get(admin('/vendor-categories'))
    return res.items || []
  },
  create: async (data) => {
    return await api.post(admin('/vendor-categories'), data)
  },
  update: async (id, data) => {
    return await api.put(admin(`/vendor-categories/${id}`), data)
  },
  delete: async (id) => {
    return await api.delete(admin(`/vendor-categories/${id}`))
  },
}

// Questionnaire (plan creation quiz) — uses /api/admin/questionnaires on backend
export const adminQuestionnairesService = {
  list: async () => {
    const res = await api.get(admin('/questionnaires'))
    return res.items || []
  },
  getById: async (id) => {
    return await api.get(admin(`/questionnaires/${id}`))
  },
  create: async (data) => {
    return await api.post(admin('/questionnaires'), data)
  },
  update: async (id, data) => {
    return await api.put(admin(`/questionnaires/${id}`), data)
  },
  delete: async (id) => {
    return await api.delete(admin(`/questionnaires/${id}`))
  }
}

/** Normalize admin/API row so UI always gets camelCase (handles snake_case or missing keys). */
function normalizeBannerRow(b) {
  if (!b || typeof b !== 'object') return null
  const id = b.id ?? b.Id ?? b.banner_id ?? b.bannerId
  if (id === undefined || id === null) return null
  return {
    id,
    title: b.title ?? '',
    imageUrl: String(b.imageUrl ?? b.image_url ?? ''),
    linkUrl: String(b.linkUrl ?? b.link_url ?? ''),
    isActive: Boolean(b.isActive ?? b.is_active ?? false),
    orderIndex: Number(b.orderIndex ?? b.order_index ?? 0),
    createdAt: b.createdAt ?? b.created_at,
    updatedAt: b.updatedAt ?? b.updated_at,
  }
}

/** Pull array from various API envelope shapes (proxy/wrapper-safe). */
function extractBannerList(res) {
  if (res == null) return []
  if (Array.isArray(res)) return res
  if (typeof res !== 'object') return []
  const raw =
    res.items ??
    res.data?.items ??
    res.banners ??
    res.data?.banners ??
    res.results ??
    (Array.isArray(res.data) ? res.data : null)
  return Array.isArray(raw) ? raw : []
}

const BANNERS_LIST_TIMEOUT_MS = 45000

export const adminBannersService = {
  list: async () => {
    const res = await api.get(admin('/banners'), { timeout: BANNERS_LIST_TIMEOUT_MS })
    const arr = extractBannerList(res)
    const normalized = arr.map(normalizeBannerRow).filter(Boolean)
    if (arr.length > 0 && normalized.length === 0) {
      console.warn(
        '[adminBanners] Server returned',
        arr.length,
        'rows but none could be normalized; check id field shape.',
        arr[0]
      )
    }
    return normalized
  },
  create: async (data) => {
    const row = await api.post(admin('/banners'), data, { timeout: PLAN_REQUEST_TIMEOUT })
    return normalizeBannerRow(row)
  },
  update: async (id, data) => {
    const row = await api.put(admin(`/banners/${id}`), data, { timeout: PLAN_REQUEST_TIMEOUT })
    return normalizeBannerRow(row)
  },
  delete: async (id) => {
    return await api.delete(admin(`/banners/${id}`))
  },
}

export const adminFeedbackService = {
  list: async (params) => {
    const res = await api.get(admin('/feedback'), { params })
    return res.items || []
  },
}

export const analyticsService = {
  getDashboardStats: async () => {
    return await adminStatsService.getStats()
  },
  getRevenueData: async (period) => {
    return { data: [] }
  },
  getUserGrowth: async (period) => {
    return { data: [] }
  }
}
